/**
 * AppPermissionsSummaryCards Component
 *
 * Six summary stat cards in a responsive grid showing OAuth app risk distribution.
 * Cards are clickable to filter the apps table by risk tier.
 */

import {
  CubeIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import type { OAuthAppSummary } from './appPermissionsTypes'

/**
 * Pre-defined Tailwind class maps for the editorial palette. The cards live on
 * a paper canvas, so accent colors are surfaced via the icon, the numeral, and
 * a 2px left rule when active — not via glow shadows. Severity hex values
 * mirror the editorial tier colors used elsewhere on the App Posture tab
 * (TIER_COLOR / TIER_DROPCAP_COLOR).
 */
const colorStyles: Record<string, {
  icon: string
  iconBg: string
  numeralColor: string
  activeRule: string
}> = {
  primary: {
    icon: 'text-[color:var(--color-secondaryBlue-300,#4F86A8)]',
    iconBg: 'bg-[color:var(--color-cream,#FBF8F2)]',
    numeralColor: 'var(--color-navy,#212938)',
    activeRule: 'var(--color-navy,#212938)',
  },
  red: {
    icon: 'text-[#C84B4B]',
    iconBg: 'bg-[#C84B4B]/10',
    numeralColor: '#C84B4B',
    activeRule: '#C84B4B',
  },
  orange: {
    icon: 'text-[#C9AD80]',
    iconBg: 'bg-[#C9AD80]/15',
    numeralColor: '#C9AD80',
    activeRule: '#C9AD80',
  },
  amber: {
    icon: 'text-[#C9AD80]',
    iconBg: 'bg-[#C9AD80]/10',
    numeralColor: '#C9AD80',
    activeRule: '#C9AD80',
  },
  emerald: {
    icon: 'text-[#4A9B6E]',
    iconBg: 'bg-[#4A9B6E]/10',
    numeralColor: '#4A9B6E',
    activeRule: '#4A9B6E',
  },
  slate: {
    icon: 'text-[color:var(--color-mute,#6B7280)]',
    iconBg: 'bg-[color:var(--color-cream,#FBF8F2)]',
    numeralColor: 'var(--color-mute,#6B7280)',
    activeRule: 'var(--color-mute,#6B7280)',
  },
}

interface CardConfig {
  key: string
  label: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  color: string
  getValue: (s: OAuthAppSummary) => number
  filterValue?: string
}

const cards: CardConfig[] = [
  { key: 'total', label: 'Total Apps', icon: CubeIcon, color: 'primary', getValue: s => s.totalApps },
  { key: 'critical', label: 'Critical', icon: ExclamationTriangleIcon, color: 'red', getValue: s => s.criticalCount, filterValue: 'Critical' },
  { key: 'high', label: 'High', icon: ExclamationCircleIcon, color: 'orange', getValue: s => s.highCount, filterValue: 'High' },
  { key: 'medium', label: 'Medium', icon: InformationCircleIcon, color: 'amber', getValue: s => s.mediumCount, filterValue: 'Medium' },
  { key: 'low', label: 'Low', icon: CheckCircleIcon, color: 'emerald', getValue: s => s.lowCount, filterValue: 'Low' },
  { key: 'stale', label: 'Stale', icon: ClockIcon, color: 'slate', getValue: s => s.staleCount },
]

interface AppPermissionsSummaryCardsProps {
  summary: OAuthAppSummary | undefined
  isLoading: boolean
  onFilterClick: (riskTier: string | undefined) => void
  activeFilter: string | undefined
}

export function AppPermissionsSummaryCards({ summary, isLoading, onFilterClick, activeFilter }: AppPermissionsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5 animate-pulse"
          >
            <div className="h-3 w-16 bg-[color:var(--color-rule,#E6E1D5)] rounded mb-3" />
            <div className="h-8 w-12 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map(card => {
        const count = summary ? card.getValue(summary) : 0
        const isActive = activeFilter === card.filterValue
        const styles = colorStyles[card.color]

        return (
          <div
            key={card.key}
            className={[
              'rounded-[4px] border bg-[color:var(--color-paper,#fff)] p-5 text-left h-full flex flex-col cursor-pointer transition-shadow duration-200',
              isActive
                ? 'border-[color:var(--color-rule,#E6E1D5)] shadow-md'
                : 'border-[color:var(--color-line,#E6E8EC)] hover:shadow-md',
            ].join(' ')}
            style={isActive ? { borderLeft: `2px solid ${styles.activeRule}` } : undefined}
            onClick={() => onFilterClick(isActive ? undefined : card.filterValue)}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
                {card.label}
              </span>
              <div className={`p-2 rounded-[4px] ${styles.iconBg}`}>
                <card.icon className={`h-4 w-4 ${styles.icon}`} />
              </div>
            </div>
            <span
              className="font-display text-3xl font-bold tracking-tight tabular-nums"
              style={{ color: styles.numeralColor }}
            >
              {count}
            </span>
          </div>
        )
      })}
    </div>
  )
}
